import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * AuraVeil "Timeless Guides" Narrator — soulful & intimate.
 *
 * Voice policy (HIGH-QUALITY ONLY):
 *   ▸ Accept voices whose name matches /(premium|natural|google)/i
 *   ▸ Reject voices whose name matches /(microsoft|default)/i
 *   ▸ Fallback: any voice matching /(natural|enhanced)/i
 *   ▸ Prefer UK / Irish English; then any English.
 *
 * Tone (both archetypes):  pitch 0.75 · rate 0.85
 *
 * Delivery:
 *   speak() splits text at .?!— into sentence-utterances and chains them
 *   via onend. A per-utterance safety timeout
 *     (wordCount / 2.2 * 1000) + 2000 ms
 *   advances the queue if onend never fires (Chrome/Safari hang bug).
 *
 * iOS / Safari unlock:
 *   `unlockNarrator()` primes the engine with a silent single-space
 *   utterance on the first user gesture. Idempotent.
 *
 * Voice resolution:
 *   The voice list is populated ONLY on `voiceschanged`, never on first
 *   synchronous getVoices() — this dodges Safari's stale initial list.
 *
 * Route change / unmount:
 *   speechSynthesis.cancel() fires automatically on every pathname
 *   change and when the provider unmounts.
 */

const NarratorContext = createContext(null);

const LS_VOICE = "auraveil.narrator.voice";
const LS_MUTED = "auraveil.narrator.muted";

const PREMIUM_HINTS = /(premium|natural|google)/i;
const BANNED_HINTS = /(microsoft|default)/i;
const FALLBACK_HINTS = /(natural|enhanced)/i;

const MALE_HINTS =
  /(daniel|arthur|oliver|george|liam|niall|fionn|conor|declan|scott|reed|david|alex|fred|matthew|brian|thomas|justin|kevin|paul|mark|william|nathan|guy|ryan|male)/i;
const FEMALE_HINTS =
  /(kate|serena|fiona|moira|catherine|libby|hazel|amelia|charlotte|olivia|samantha|zira|karen|susan|joanna|allison|tessa|victoria|amy|jenny|aria|nicky|nova|emma|salli|kimberly|ivy|linda|heather|natalia|ava|sonia|mia|female)/i;

const VOICE_TUNING = {
  male: { pitch: 0.75, rate: 0.85 },
  female: { pitch: 0.75, rate: 0.85 },
};

// -------- iOS/Safari one-time engine unlock (module-level, idempotent) --------
let _unlocked = false;
export function unlockNarrator() {
  if (_unlocked) return;
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  try {
    const primer = new SpeechSynthesisUtterance(" ");
    primer.volume = 0; // silent
    primer.rate = 1;
    window.speechSynthesis.speak(primer);
    _unlocked = true;
  } catch (_) {
    /* noop */
  }
}

// -------- Sentence splitting on . ? ! — ---------------------------------------
function splitSentences(text) {
  // Keep terminator with the sentence
  const re = /[^.?!—]+[.?!—]?/g;
  const out = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    const s = m[0].trim();
    if (s) out.push(s);
  }
  return out;
}

function countWords(s) {
  const m = s.match(/\S+/g);
  return m ? m.length : 0;
}

export function NarratorProvider({ children }) {
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const location = useLocation();

  const [voiceGender, setVoiceGenderState] = useState(() => {
    if (typeof window === "undefined") return "female";
    return localStorage.getItem(LS_VOICE) || "female";
  });
  const [muted, setMutedState] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(LS_MUTED) === "1";
  });

  const [voices, setVoices] = useState([]); // populated ONLY on voiceschanged
  const [voicesReady, setVoicesReady] = useState(false);
  const [currentUtteranceId, setCurrentUtteranceId] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const sessionRef = useRef(0);
  const safetyTimerRef = useRef(null);

  // Resolve voices ONLY after voiceschanged fires (not on synchronous first load).
  useEffect(() => {
    if (!supported) return;
    const handler = () => {
      const list = window.speechSynthesis.getVoices() || [];
      if (list.length) {
        setVoices(list);
        setVoicesReady(true);
      }
    };
    window.speechSynthesis.addEventListener("voiceschanged", handler);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", handler);
    };
  }, [supported]);

  useEffect(() => {
    localStorage.setItem(LS_VOICE, voiceGender);
  }, [voiceGender]);
  useEffect(() => {
    localStorage.setItem(LS_MUTED, muted ? "1" : "0");
  }, [muted]);

  const clearSafetyTimer = () => {
    if (safetyTimerRef.current) {
      clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = null;
    }
  };

  const pickVoice = useCallback(
    (gender) => {
      if (!voicesReady || !voices.length) return null;

      const premium = voices.filter(
        (v) => PREMIUM_HINTS.test(v.name) && !BANNED_HINTS.test(v.name),
      );
      const fallback = voices.filter(
        (v) => FALLBACK_HINTS.test(v.name) && !BANNED_HINTS.test(v.name),
      );

      const chooseFrom = (pool) => {
        if (!pool.length) return null;
        const gb = pool.filter((v) =>
          /en[-_]?(GB|IE|SCT|WLS)/i.test(v.lang),
        );
        const enAny = pool.filter((v) => /^en/i.test(v.lang));
        const genderHints = gender === "male" ? MALE_HINTS : FEMALE_HINTS;
        const antiHints = gender === "male" ? FEMALE_HINTS : MALE_HINTS;

        for (const tier of [gb, enAny, pool]) {
          if (!tier.length) continue;
          const gendered = tier.filter(
            (v) => genderHints.test(v.name) && !antiHints.test(v.name),
          );
          if (gendered.length) return gendered[0];
        }
        return gb[0] || enAny[0] || pool[0];
      };

      return chooseFrom(premium) || chooseFrom(fallback) || null;
    },
    [voices, voicesReady],
  );

  const hasNaturalVoice = useMemo(() => {
    if (!voicesReady || !voices.length) return false;
    return voices.some(
      (v) =>
        (PREMIUM_HINTS.test(v.name) || FALLBACK_HINTS.test(v.name)) &&
        !BANNED_HINTS.test(v.name),
    );
  }, [voices, voicesReady]);

  const stop = useCallback(() => {
    if (!supported) return;
    sessionRef.current += 1;
    clearSafetyTimer();
    try {
      window.speechSynthesis.cancel();
    } catch (_) {
      /* noop */
    }
    setIsSpeaking(false);
    setCurrentUtteranceId(null);
  }, [supported]);

  const speak = useCallback(
    (text, id) => {
      if (!supported || muted || !text || !id) return;
      sessionRef.current += 1;
      clearSafetyTimer();
      try {
        window.speechSynthesis.cancel();
      } catch (_) {
        /* noop */
      }
      const mySession = sessionRef.current;

      const sentences = splitSentences(text);
      if (!sentences.length) return;

      const voice = pickVoice(voiceGender);
      const { pitch, rate } = VOICE_TUNING[voiceGender] || VOICE_TUNING.female;

      setCurrentUtteranceId(id);
      setIsSpeaking(true);

      const finishChain = () => {
        if (sessionRef.current !== mySession) return;
        clearSafetyTimer();
        setIsSpeaking(false);
        setCurrentUtteranceId((prev) => (prev === id ? null : prev));
      };

      const speakIndex = (i) => {
        if (sessionRef.current !== mySession) return;
        if (i >= sentences.length) {
          finishChain();
          return;
        }
        const sentence = sentences[i];
        const utter = new SpeechSynthesisUtterance(sentence);
        if (voice) utter.voice = voice;
        utter.pitch = pitch;
        utter.rate = rate;
        utter.volume = 1;

        let advanced = false;
        const advance = () => {
          if (advanced) return;
          advanced = true;
          if (sessionRef.current !== mySession) return;
          clearSafetyTimer();
          speakIndex(i + 1);
        };

        utter.onend = advance;
        utter.onerror = advance;

        // Safety timeout: (wordCount / 2.2 * 1000) + 2000 ms
        const words = countWords(sentence);
        const safetyMs = (words / 2.2) * 1000 + 2000;
        safetyTimerRef.current = setTimeout(advance, safetyMs);

        try {
          window.speechSynthesis.speak(utter);
        } catch (_) {
          advance();
        }
      };

      speakIndex(0);
    },
    [supported, muted, voiceGender, pickVoice],
  );

  const setVoiceGender = useCallback(
    (g) => {
      if (g !== "male" && g !== "female") return;
      setVoiceGenderState(g);
      if (isSpeaking) stop();
    },
    [isSpeaking, stop],
  );

  const setMuted = useCallback(
    (v) => {
      setMutedState(v);
      if (v) stop();
    },
    [stop],
  );

  const toggleMute = useCallback(() => setMuted(!muted), [muted, setMuted]);

  // Cancel narration on every route change
  useEffect(() => {
    if (!supported) return;
    sessionRef.current += 1;
    clearSafetyTimer();
    try {
      window.speechSynthesis.cancel();
    } catch (_) {
      /* noop */
    }
    setIsSpeaking(false);
    setCurrentUtteranceId(null);
  }, [location.pathname, supported]);

  // Cancel narration on provider unmount
  useEffect(() => {
    return () => {
      if (!supported) return;
      sessionRef.current += 1;
      clearSafetyTimer();
      try {
        window.speechSynthesis.cancel();
      } catch (_) {
        /* noop */
      }
    };
  }, [supported]);

  const value = useMemo(
    () => ({
      supported,
      voiceGender,
      setVoiceGender,
      muted,
      setMuted,
      toggleMute,
      speak,
      stop,
      currentUtteranceId,
      isSpeaking,
      hasNaturalVoice,
      unlock: unlockNarrator,
    }),
    [
      supported,
      voiceGender,
      setVoiceGender,
      muted,
      setMuted,
      toggleMute,
      speak,
      stop,
      currentUtteranceId,
      isSpeaking,
      hasNaturalVoice,
    ],
  );

  return (
    <NarratorContext.Provider value={value}>
      {children}
    </NarratorContext.Provider>
  );
}

export function useNarrator() {
  const ctx = useContext(NarratorContext);
  if (!ctx) {
    throw new Error("useNarrator must be used inside <NarratorProvider>");
  }
  return ctx;
}
